import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react"
import { absoluteHref } from "@/lib/embed/absoluteHref"
import {
  buildAgentHrefFromAutocomplete,
  buildCityAgentsHref,
  buildCityOfficesHref,
  buildEntityRosterHref,
  buildOfficeHrefFromAutocomplete,
} from "@/lib/embed/buildEntityHref"
import type {
  AgentAutocompleteResult,
  AutocompleteCategory,
  CityAutocompleteResult,
  EntityType,
  OfficeAutocompleteResult,
} from "@/types/entitySearch"
import {
  DEBOUNCE_MS,
  debounce,
  fetchAgentAutocomplete,
  fetchOfficeAutocomplete,
  MIN_QUERY,
} from "../lib/fetchAutocomplete"
import { useFocusTrap } from "../hooks/useFocusTrap"
import type { EmbedUi } from "../types"
import { AutocompleteShell } from "./AutocompleteShell"
import { ResultsList } from "./ResultsList"

type AgentAutocompleteProps = {
  searchBaseUrl: string
  entityType?: EntityType
  placeholder: string
  variant?: EmbedUi
  onClose: () => void
  onRedirect: (href: string) => void
}

type FlatResult = {
  category: string
  result:
    | AgentAutocompleteResult
    | OfficeAutocompleteResult
    | CityAutocompleteResult
}

export function AgentAutocomplete({
  searchBaseUrl,
  entityType = "agent",
  placeholder,
  variant = "lightbox",
  onClose,
  onRedirect,
}: AgentAutocompleteProps) {
  const [query, setQuery] = useState("")
  const [categories, setCategories] = useState<AutocompleteCategory[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [expanded, setExpanded] = useState(false)
  const listboxId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const isDropdown = variant === "dropdown"
  useFocusTrap(containerRef, !isDropdown)

  const flatResults = useMemo<FlatResult[]>(
    () =>
      categories.flatMap((category) =>
        category.Results.map((result) => ({
          category: category.CategoryName,
          result,
        }))
      ),
    [categories]
  )

  const runSearch = useMemo(
    () =>
      debounce(async (value: string) => {
        if (value.trim().length < MIN_QUERY) {
          setCategories([])
          setLoading(false)
          setError(null)
          return
        }
        setLoading(true)
        setError(null)
        try {
          const fetchAutocomplete =
            entityType === "office" ? fetchOfficeAutocomplete : fetchAgentAutocomplete
          const data = await fetchAutocomplete(searchBaseUrl, value)
          setCategories(Array.isArray(data) ? data : [])
        } catch {
          setCategories([])
          setError("Search unavailable — try again.")
        } finally {
          setLoading(false)
        }
      }, DEBOUNCE_MS),
    [entityType, searchBaseUrl]
  )

  useEffect(() => {
    if (!isDropdown) {
      inputRef.current?.focus()
    }
  }, [isDropdown])

  useEffect(() => {
    setHighlightedIndex(-1)
    runSearch(query)
  }, [query, runSearch])

  const redirectForResult = useCallback(
    (category: string, result: FlatResult["result"]) => {
      if (category === "Agent") {
        const href = buildAgentHrefFromAutocomplete(result as AgentAutocompleteResult)
        if (href) {
          onRedirect(absoluteHref(searchBaseUrl, href))
        }
        return
      }
      if (category === "Office") {
        const href = buildOfficeHrefFromAutocomplete(result as OfficeAutocompleteResult)
        if (href) {
          onRedirect(absoluteHref(searchBaseUrl, href))
        }
        return
      }
      if (category === "City") {
        const buildCityHref =
          entityType === "office" ? buildCityOfficesHref : buildCityAgentsHref
        onRedirect(
          absoluteHref(
            searchBaseUrl,
            buildCityHref(result as CityAutocompleteResult)
          )
        )
      }
    },
    [entityType, onRedirect, searchBaseUrl]
  )

  const selectResult = useCallback(
    (index: number) => {
      const selected = flatResults[index]
      if (selected) {
        redirectForResult(selected.category, selected.result)
      }
    },
    [flatResults, redirectForResult]
  )

  const redirectToRoster = useCallback(() => {
    onRedirect(
      absoluteHref(searchBaseUrl, buildEntityRosterHref(entityType, query))
    )
  }, [entityType, onRedirect, query, searchBaseUrl])

  const handleEnter = useCallback(() => {
    if (highlightedIndex >= 0) {
      selectResult(highlightedIndex)
      return
    }
    redirectToRoster()
  }, [highlightedIndex, redirectToRoster, selectResult])

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault()
      if (isDropdown) {
        setExpanded(false)
        return
      }
      onClose()
      return
    }
    if (event.key === "ArrowDown") {
      event.preventDefault()
      setHighlightedIndex((prev) =>
        prev < flatResults.length - 1 ? prev + 1 : prev
      )
      return
    }
    if (event.key === "ArrowUp") {
      event.preventDefault()
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1))
      return
    }
    if (event.key === "Enter") {
      event.preventDefault()
      handleEnter()
    }
  }

  const trimmedQuery = query.trim()
  const showFooter = trimmedQuery.length >= MIN_QUERY && !error

  const dialogLabel = entityType === "office" ? "Office search" : "Agent search"
  const rosterLabel =
    entityType === "office" ? "View all matching offices" : "View all matching agents"

  const items = flatResults.map((item, index) => ({
    key: `${item.category}-${index}`,
    title:
      item.category === "Agent"
        ? (item.result as AgentAutocompleteResult).MemberFullName
        : item.category === "Office"
          ? (item.result as OfficeAutocompleteResult).OfficeName
          : (item.result as CityAutocompleteResult).DisplayName,
    meta: item.category,
  }))

  return (
    <AutocompleteShell
      variant={variant}
      ariaLabel={dialogLabel}
      containerRef={containerRef}
      inputRef={inputRef}
      listboxId={listboxId}
      highlightedIndex={highlightedIndex}
      placeholder={placeholder}
      query={query}
      onQueryChange={(value) => {
        setQuery(value)
        setExpanded(true)
      }}
      onKeyDown={handleKeyDown}
      onFocus={() => setExpanded(true)}
      onFocusLeave={() => setExpanded(false)}
      onClose={onClose}
      showResults={!isDropdown || (expanded && trimmedQuery.length >= MIN_QUERY)}
      results={
        <ResultsList
          items={items}
          loading={loading}
          error={error}
          query={query}
          highlightedIndex={highlightedIndex}
          listboxId={listboxId}
          onHighlight={setHighlightedIndex}
          onSelect={selectResult}
        />
      }
      footer={
        showFooter ? (
          <div className="bpp-footer">
            <button type="button" onClick={redirectToRoster}>
              {rosterLabel} for &ldquo;{trimmedQuery}&rdquo;
            </button>
          </div>
        ) : undefined
      }
    />
  )
}
